import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, CarFront, Copy, Download, FilePlus2, History, MoreHorizontal, RefreshCcw, Route, Send, ThumbsDown, Users } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { PageHeader, Panel, StatusBadge, KvRow, EmptyState, Skeleton, money, shortDate, btnPrimary, btnSecondary } from "@/components/admin/ui";

function ActionsMenu({ onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const items = [
    ["pdf", Download, "Télécharger le PDF"],
    ["copy", Copy, "Copier le lien client"],
    ["duplicate", RefreshCcw, "Dupliquer"],
    ["reject", ThumbsDown, "Marquer refusé"],
  ];
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="quote-actions-button" className={btnSecondary}>
        <MoreHorizontal className="h-4 w-4" strokeWidth={2} /> Actions
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-30 w-56 overflow-hidden rounded-xl border border-ink/[0.07] bg-white py-1 shadow-lg" data-testid="quote-actions-menu">
          {items.map(([key, Icon, label]) => (
            <button key={key} onClick={() => { setOpen(false); onAction(key); }} data-testid={`action-${key}`}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink transition-colors hover:bg-cloud">
              <Icon className="h-4 w-4 text-slate" strokeWidth={1.5} /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get(`/quotes/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Devis introuvable"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!data) return <Skeleton rows={8} />;
  const { quote: q, events } = data;
  const editable = !["accepted", "converted", "rejected"].includes(q.status);

  const action = async (key) => {
    if (key === "pdf") { window.open(`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/quotes/${id}/pdf`, "_blank"); return; }
    if (key === "copy") {
      await navigator.clipboard.writeText(`${window.location.origin}/devis/${q.public_token}`);
      toast.success("Lien client copié");
      return;
    }
    if (key === "duplicate") {
      const r = await adminApi.post(`/quotes/${id}/duplicate`);
      toast.success("Devis dupliqué");
      navigate(`/admin/app/quotes/${r.data.quote.id}`);
      return;
    }
    if (key === "reject") {
      await adminApi.post(`/quotes/${id}/status`, { status: "rejected" });
      toast.success("Devis marqué refusé");
      load();
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/quotes/${id}/send`, { origin_url: window.location.origin });
      toast.success(`Devis envoyé à ${r.data.sent_to}`);
      load();
    } catch (err) { toast.error(apiError(err, "Envoi impossible")); }
    finally { setBusy(false); }
  };

  const convertInvoice = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/quotes/${id}/convert-invoice`);
      toast.success("Facture créée — vérifiez-la puis émettez-la");
      navigate(`/admin/app/invoices/${r.data.invoice.id}`);
    } catch (err) { toast.error(apiError(err, "Conversion impossible")); }
    finally { setBusy(false); }
  };

  const convertBooking = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/quotes/${id}/convert-booking`);
      toast.success(`Réservation ${r.data.booking.tracking_code} créée`);
      navigate(`/admin/app/bookings/${r.data.booking.id}`);
    } catch (err) { toast.error(apiError(err, "Conversion impossible")); }
    finally { setBusy(false); }
  };

  const t = q.transport || {};
  const vehicle = { standard: "Standard Class", business: "Business Class", first: "First Class", van: "Van" }[t.vehicle_category];

  return (
    <div data-testid="admin-quote-detail">
      <Link to="/admin/app/quotes" className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate transition-colors hover:text-ink">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Devis
      </Link>
      <PageHeader
        title={q.number}
        subtitle={`${q.customer?.name} · version ${q.version || 1}`}
        badge={<StatusBadge status={q.status} />}
        actions={<div className="flex items-center gap-2">
          {editable && <Link to={`/admin/app/quotes/${id}/edit`} className={btnSecondary} data-testid="quote-edit-link">Modifier</Link>}
          {["draft", "sent", "viewed", "expired"].includes(q.status) && (
            <button onClick={send} disabled={busy} data-testid="quote-send-button" className={`${btnPrimary} disabled:opacity-50`}>
              <Send className="h-4 w-4" strokeWidth={2} /> {q.status === "draft" ? "Envoyer au client" : "Renvoyer"}</button>)}
          {q.status === "accepted" && (<>
            <button onClick={convertInvoice} disabled={busy} data-testid="quote-convert-invoice" className={`${btnPrimary} disabled:opacity-50`}>
              <FilePlus2 className="h-4 w-4" strokeWidth={2} /> Créer la facture</button>
            <button onClick={convertBooking} disabled={busy} data-testid="quote-convert-booking" className={`${btnSecondary} disabled:opacity-50`}>
              Créer la réservation</button>
          </>)}
          <ActionsMenu onAction={action} />
        </div>} />

      {q.status === "accepted" && q.acceptance && (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="acceptance-banner">
          Accepté en ligne par <strong>{q.acceptance.name}</strong> le {shortDate(q.acceptance.at)} — version {q.acceptance.version}.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          {(t.pickup || q.service_info?.route) && (
            <Panel title="Trajet" testId="quote-transport">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {t.pickup && <p className="flex items-start gap-2"><Route className="mt-0.5 h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} />
                  <span>{t.pickup} → {t.dropoff || "Mise à disposition"}</span></p>}
                {t.date && <p className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t.date}{t.time ? ` · ${t.time}` : ""}</p>}
                {t.return_date && <p className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-slate" strokeWidth={1.5} /> Retour : {t.return_date}{t.return_time ? ` · ${t.return_time}` : ""}</p>}
                {vehicle && <p className="flex items-center gap-2"><CarFront className="h-4 w-4 text-miles" strokeWidth={1.5} /> {vehicle}</p>}
                {t.passengers && <p className="flex items-center gap-2"><Users className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t.passengers} passager{t.passengers > 1 ? "s" : ""}{t.luggage ? ` · ${t.luggage} bagage${t.luggage > 1 ? "s" : ""}` : ""}</p>}
                {t.passenger_name && <p className="text-slate">Passager : {t.passenger_name}</p>}
              </div>
            </Panel>
          )}

          <Panel title="Proposition" testId="quote-lines">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink/[0.07] text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-slate/60">
                  <th className="pb-2">Description</th>
                  <th className="pb-2 text-right">Qté</th>
                  <th className="pb-2 text-right">Prix unit.</th>
                  <th className="pb-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(q.lines || []).map((l, i) => (
                  <tr key={i} className="border-b border-ink/[0.04]">
                    <td className="py-2.5 pr-2 font-medium text-ink">
                      {l.description}
                      {l.discount_value > 0 && <p className="text-xs text-emerald-600">Remise {l.discount_type === "percent" ? `−${l.discount_value} %` : `−${money(l.discount_value)}`}</p>}
                    </td>
                    <td className="py-2.5 text-right font-mono text-slate">{l.qty}</td>
                    <td className="py-2.5 text-right font-mono text-slate">{l.unit_price === 0 ? "Offert" : money(l.unit_price)}</td>
                    <td className="py-2.5 text-right font-mono font-semibold text-ink">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ml-auto mt-4 w-64 space-y-1.5 border-t border-ink/[0.07] pt-3 text-sm">
              {q.vat_registered ? (<>
                <div className="flex justify-between text-slate"><span>Sous-total HT</span><span className="font-mono">{money(q.gross ?? q.net)}</span></div>
                {q.discount_total > 0 && <>
                  <div className="flex justify-between text-slate"><span>Remise</span><span className="font-mono">−{money(q.discount_total)}</span></div>
                  <div className="flex justify-between text-slate"><span>Net HT</span><span className="font-mono">{money(q.net)}</span></div>
                </>}
                <div className="flex justify-between text-slate"><span>TVA ({q.vat_rate} %)</span><span className="font-mono">{money(q.vat)}</span></div>
              </>) : <p className="text-xs text-slate">TVA non applicable — entreprise non assujettie.</p>}
              <div className="flex justify-between rounded-lg bg-miles px-3 py-2 font-bold text-white">
                <span>Total proposé</span><span className="font-mono" data-testid="quote-total">{money(q.total)}</span>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate">Valable jusqu&apos;au <strong className="text-ink">{shortDate(q.valid_until)}</strong></p>
            {q.conditions && <p className="mt-2 rounded-xl bg-cloud px-4 py-3 text-xs text-slate">{q.conditions}</p>}
          </Panel>
        </div>

        <div className="space-y-6 lg:col-span-4">
          <Panel title="Client" testId="quote-customer">
            <p className="font-semibold text-ink">{q.customer?.name}</p>
            {q.customer?.email && <p className="mt-1 text-sm text-slate">{q.customer.email}</p>}
            {q.customer?.phone && <p className="text-sm text-slate">{q.customer.phone}</p>}
            {q.customer_id && <Link to={`/admin/app/customers/${q.customer_id}`} className="mt-2 inline-block text-sm font-medium text-miles">Voir la fiche client →</Link>}
            {q.converted_invoice_id && <KvRow label="Facture" mono><Link className="text-miles" to={`/admin/app/invoices/${q.converted_invoice_id}`}>Voir la facture →</Link></KvRow>}
            {q.converted_booking_id && <KvRow label="Réservation" mono><Link className="text-miles" to={`/admin/app/bookings/${q.converted_booking_id}`}>Voir la réservation →</Link></KvRow>}
          </Panel>

          {(q.versions || []).length > 0 && (
            <Panel title="Versions" testId="quote-versions">
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="font-medium text-miles">v{q.version || 1} — actuelle</span><span className="font-mono text-ink">{money(q.total)}</span></div>
                {q.versions.map((v) => (
                  <div key={v.version} className="flex justify-between text-sm text-slate">
                    <span>v{v.version} — {shortDate(v.at)}</span><span className="font-mono">{money(v.total)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title={<span className="flex items-center gap-1.5"><History className="h-4 w-4" strokeWidth={1.5} /> Historique</span>} testId="quote-history">
            {(events || []).length === 0 ? <EmptyState title="Aucun événement" testId="no-events" /> : (
              <div className="space-y-3">
                {events.map((e) => (
                  <div key={e.id} className="flex gap-3 text-sm">
                    <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-miles" />
                    <div>
                      <p className="text-ink">{e.details || e.event}</p>
                      <p className="text-xs text-slate">{shortDate(e.at)} · {e.actor}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
